import CheckIcon from '@mui/icons-material/Check';
import { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { PlanCard } from 'src/components';
import { useCurrentUser } from 'src/hooks';
// import { createStripePortalSession } from 'src/api/subscriptions/createStripePortalSession';
import { useScrollToTop } from 'src/hooks';

export const Upgrade = () => {
  const { user } = useCurrentUser();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  useScrollToTop();

  // Get promo code from URL if it exists
  const promoCode = searchParams.get('promo');

  // Plan data - move button logic to the component
  const plans = [
    {
      id: 'free',
      title: { i18nKey: 'my_account.tier.plus_free' },
      titleColor: 'text-primary',
      cartHistory: 'upgrade.five_latest_carts',
      users: '1',
      price: '0',
      isDark: false,
      height: 'medLg:h-[492px]',
    },
    {
      id: 'personal',
      title: { i18nKey: 'upgrade.plus_personal' },
      titleColor: 'text-[#26CA99]',
      cartHistory: 'upgrade.full',
      users: '1',
      price: '9.99',
      isDark: true,
      height: 'medLg:h-[524px]',
    },
    {
      id: 'organization',
      title: 'Organization',
      titleColor: 'text-[#F1B820]',
      cartHistory: 'upgrade.full',
      users: 'Multiple',
      price: '250',
      isDark: false,
      height: 'medLg:h-[492px]',
    },
  ];

  // Memoize the button rendering logic
  const renderButtonAction = useCallback(
    (planId: string) => {
      // if user not signed in, show sign up button
      if (!user) {
        return (
          <Link to='/login' className='w-full medLg:w-auto'>
            <button className='button-primary h-12 w-full'>
              <span className='font-secondary text-base font-bold text-offWhite'>
                {t('upgrade.sign_up')}
              </span>
            </button>
          </Link>
        );
      }

      const userSubTier = user.subscription?.tier;

      if (planId === 'free') {
        if (userSubTier === 'free') {
          return (
            <div className='inline-flex h-12 w-full items-center justify-center gap-1 medLg:w-auto'>
              <CheckIcon style={{ color: '#04033C' }} />
              <span className='font-secondary text-base font-bold'>
                {t('upgrade.subscribed')}
              </span>
            </div>
          );
        } else {
          return <div className='h-12 w-full opacity-0'></div>;
        }
      }

      if (planId === 'personal') {
        if (userSubTier === 'personal') {
          return (
            <Link
              to='/plus/manage-subscription'
              className='button-primary h-12 w-full medLg:w-auto'
            >
              <button className='font-secondary text-base font-bold text-offWhite'>
                <span className='hidden xs:block'>
                  {t('common.manage_subscription')}
                </span>
                <span className='block xs:hidden'>{t('upgrade.manage')}</span>
              </button>
            </Link>
          );
        }

        if (userSubTier === 'organization') {
          return <div className='h-12 w-full opacity-0'></div>;
        }

        const subscribeUrl = promoCode
          ? `/plus/subscribe?plan=personal&promo=${promoCode}`
          : '/plus/subscribe?plan=personal';

        return (
          <Link to={subscribeUrl} className='w-full medLg:w-auto'>
            <button className='button-primary h-12 w-full font-secondary text-base font-bold text-offWhite'>
              Upgrade
            </button>
          </Link>
        );
      }

      if (planId === 'organization') {
        if (userSubTier === 'organization') {
          return (
            <Link
              to='/plus/manage-subscription'
              className='button-secondary h-12 w-full font-secondary text-base font-bold text-primary-dark medLg:w-auto'
            >
              <span className='hidden xs:block'>
                {t('common.manage_subscription')}
              </span>
              <span className='block xs:hidden'>{t('upgrade.manage')}</span>
            </Link>
          );
        }

        if (userSubTier === 'personal') {
          return (
            <a
              href={`/about#form`}
              className='button-secondary h-12 w-full font-secondary text-base font-bold text-primary-dark medLg:w-auto'
            >
              Talk to Us
            </a>
            // <button
            //   onClick={() => createStripePortalSession()}
            //   className='button-secondary h-12 w-full font-secondary text-base font-bold text-primary-dark medLg:w-auto'
            // >
            //   Upgrade
            // </button>
          );
        }

        return (
          <a
            href={`/about#form`}
            className='button-secondary h-12 w-full font-secondary text-base font-bold text-primary-dark medLg:w-auto'
          >
            Talk to Us
          </a>
          // <Link to='/plus/subscribe?plan=organization' className='w-full medLg:w-auto'>
          //   <button className='button-secondary h-12 w-full font-secondary text-base font-bold text-primary-dark'>
          //     Upgrade
          //   </button>
          // </Link>
        );
      }

      return null;
    },
    [user, t, promoCode],
  );

  return (
    <div className='flex w-full flex-col items-center justify-center gap-8 bg-cloudGray pb-16 pt-12 medLg:gap-16 medLg:pb-32 medLg:pt-24'>
      <h1 className='text-center font-primary text-2xl font-bold text-[#161616] medLg:text-3xl'>
        <Trans
          i18nKey={t('upgrade.sac_plus_pricing')}
          components={[<span className='text-primary'></span>]}
        />
      </h1>

      <div className='flex w-full flex-col items-center justify-center gap-8 bg-cloudGray px-6 medLg:flex-row'>
        {plans.map((plan) => (
          <PlanCard
            key={plan.id}
            {...plan}
            buttonAction={renderButtonAction(plan.id)}
          />
        ))}
      </div>
    </div>
  );
};
